/* cSpell:ignore regexify */

import React from 'react';
import { StyleProp, StyleSheet, Text, TextStyle } from 'react-native';

import regexifyString from 'regexify-string';

import { PhoneLink } from '../../PhoneLink';
import { TextLink } from '../../TextLink';

export const TextLinkify = (props: TextLinkifyProps) => (
  <Text>{linkifyText(props.children, props.regex)}</Text>
);

export const TextLinkifyTags = ({ children, links, palette, ...rest }: TextLinkifyTagsProps) => (
  <Text {...rest}>
    {linkifyTags({
      text: children,
      links,
      size: rest.size,
      sizeMatch: rest.sizeMatch,
      style: rest.style,
      palette,
    })}
  </Text>
);

/**
 * This component aims to automatically extract and convert contentful link markdown to a text link.
 * Contentful link markdown generates texts in the following format:
 * We work with 200+ farms to source the freshest seasonal ingredients, [learn more](https://www.sweetgreen.com/mission#our-mission).
 */
export const ContentfulTextLinkify = ({ children, palette, ...rest }: TextLinkifyProps) => (
  <Text {...rest}>
    {linkifyContentfulMarkdown({
      text: children,
      size: rest.size,
      sizeMatch: rest.sizeMatch,
      style: rest.style,
      palette,
      underline: rest.underline,
    })}
  </Text>
);

// ─── UTILS ──────────────────────────────────────────────────────────────────────

function linkifyText(text: string, urlRegex = /(((https?:\/\/)|(www\.))\S+)/g) {
  return regexifyString({
    pattern: urlRegex,
    decorator: (url, index) => (
      <TextLink key={index} href={withHttpsProtocol(url)}>
        {url}
      </TextLink>
    ),
    input: text,
  }).filter(withoutEmptyStrings);
}

function linkifyTags(linkifyTagsParams: LinkifyTagsParams) {
  const { text, links, palette, size, sizeMatch, style } = linkifyTagsParams;

  return regexifyString({
    pattern: /{(.+?)}|<b>(.+?)<\/b>/g,
    decorator(tag, index) {
      if (HIGHLIGHT_PATTERN.test(tag)) {
        return (
          <Text key={index} style={[style, styles.highlight]}>
            {tag.replace('<b>', '').replace('</b>', '')}
          </Text>
        );
      }

      const currentTag = links.find((link) => link.tag === tag.slice(1, -1));

      if (!currentTag) {
        return '';
      }

      // Phone links require special treatment for native devices.
      if (currentTag.href?.startsWith('tel')) {
        return (
          <PhoneLink
            key={index}
            protocol="tel"
            size={size}
            sizeMatch={sizeMatch}
            phoneNumber={currentTag.href.split('tel:')[1]}
          >
            {currentTag.description}
          </PhoneLink>
        );
      }

      return (
        <TextLink
          key={index}
          size={size}
          sizeMatch={sizeMatch}
          style={style}
          href={currentTag.href}
          underline={currentTag.underline}
          palette={palette}
          onPress={currentTag.onPress}
        >
          {currentTag.description}
        </TextLink>
      );
    },
    input: text,
  }).filter(withoutEmptyStrings);
}

function linkifyContentfulMarkdown(
  linkifyContentfulMarkdownParams: LinkifyContentfulMarkdownParams,
) {
  const { text, palette, size, sizeMatch, style, underline } = linkifyContentfulMarkdownParams;

  return regexifyString({
    pattern: CONTENTFUL_MARKDOWN_LINK_PATTERN,
    decorator(markdownLink, index) {
      const match = markdownLink.match(CONTENTFUL_MARKDOWN_LINK_CAPTURE_PATTERN);

      if (!match) {
        return markdownLink;
      }

      const [, label, href] = match;

      return (
        <TextLink
          key={index}
          size={size}
          sizeMatch={sizeMatch}
          style={style}
          href={withHttpsProtocol(href)}
          underline={underline}
          palette={palette}
        >
          {label}
        </TextLink>
      );
    },
    input: text,
  }).filter(withoutEmptyStrings);
}

function withoutEmptyStrings(chunk: string | React.ReactElement) {
  return chunk !== '';
}

export function withHttpsProtocol(url: string) {
  if (url.startsWith('http')) {
    return url;
  }

  return `https://${url}`;
}

// ─── Constants ───────────────────────────────────────────────────────────────

const HIGHLIGHT_PATTERN = /<b>(.+?)<\/b>/;
const CONTENTFUL_MARKDOWN_LINK_CAPTURE_PATTERN = /\[([^\]]+)\]\(([^)]+)\)/;
const CONTENTFUL_MARKDOWN_LINK_PATTERN = /\[([^\]]+)\]\(([^)]+)\)/g;

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  highlight: {
    fontWeight: '700',
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type TextLinkifyProps = Omit<React.ComponentProps<typeof TextLink>, 'style'> &
  Readonly<{
    children: string;
    style?: StyleProp<TextStyle>;
    regex?: RegExp;
  }>;

type TextLinkifyTagsProps = TextLinkifyProps &
  Readonly<{
    links: ReadonlyArray<
      Readonly<{
        tag: string;
        description: string;
        href?: string;
        underline?: boolean;
        newTab?: boolean;
        onPress?: () => void;
      }>
    >;
  }>;

type LinkifyTagsParams = {
  text: string;
  links: TextLinkifyTagsProps['links'];
} & Pick<TextLinkifyProps, 'size' | 'sizeMatch' | 'style' | 'palette'>;

type LinkifyContentfulMarkdownParams = {
  text: string;
} & Pick<TextLinkifyProps, 'size' | 'sizeMatch' | 'style' | 'palette' | 'underline'>;
